import { Component, OnInit, inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowLeft,
  faBuilding,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faCreditCard,
  faCalendarAlt,
  faPen,
  faBriefcase,
  faHandshake,
  faCircleInfo,
  faClock,
  faUsers,
  faTags,
  faLock,
  faPercent,
  faArrowsRotate,
  faNoteSticky,
  faCommentDots,
  faFileContract,
  faCalendarDays,
  faClockRotateLeft,
  faGaugeHigh,
  faUserPlus,
  faFileInvoiceDollar,
  faScrewdriverWrench,
  faHeadset,
  faArrowRight,
  faIdCard,
  faCaretDown,
  faCheck,
  faTriangleExclamation,
  faRotateLeft,
} from '@fortawesome/pro-solid-svg-icons';
import { PartnerService } from '../../../../core/services/partner.service';
import { Partner } from '../../../../core/models/partner.model';
import { EditPartnerModal } from '../components/edit-partner-modal/edit-partner-modal';
import { PartnerFilesModalComponent } from '../components/partner-files-modal/partner-files-modal';
import { PartnerFile } from '../../../../core/models/partner-file.model';
import { PopupInfoService } from '../../../../shared/services/popup-info.service';
import { faFile, faDownload, faTrash } from '@fortawesome/pro-solid-svg-icons';
import {
  PartnerConditionLog,
  PARTNER_CONDITION_FIELD_LABELS,
} from '../../../../core/models/partner-condition-log.model';

@Component({
  selector: 'app-partner-detail-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FontAwesomeModule,
    EditPartnerModal,
    PartnerFilesModalComponent,
  ],
  templateUrl: './partner-detail.html',
  styleUrls: ['./partner-detail.scss'],
})
export class PartnerDetailPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly partnerService = inject(PartnerService);
  private readonly popupInfoService = inject(PopupInfoService);

  faArrowLeft = faArrowLeft;
  faBuilding = faBuilding;
  faPhone = faPhone;
  faEnvelope = faEnvelope;
  faMapMarkerAlt = faMapMarkerAlt;
  faCreditCard = faCreditCard;
  faCalendarAlt = faCalendarAlt;
  faPen = faPen;
  faBriefcase = faBriefcase;
  faIdCard = faIdCard;
  faHandshake = faHandshake;
  faCircleInfo = faCircleInfo;
  faClock = faClock;
  faUsers = faUsers;
  faTags = faTags;
  faLock = faLock;
  faFile = faFile;
  faDownload = faDownload;
  faTrash = faTrash;
  faPercent = faPercent;
  faArrowsRotate = faArrowsRotate;
  faNoteSticky = faNoteSticky;
  faCommentDots = faCommentDots;
  faFileContract = faFileContract;
  faCalendarDays = faCalendarDays;
  faClockRotateLeft = faClockRotateLeft;
  faGaugeHigh = faGaugeHigh;
  faUserPlus = faUserPlus;
  faFileInvoiceDollar = faFileInvoiceDollar;
  faScrewdriverWrench = faScrewdriverWrench;
  faHeadset = faHeadset;
  faArrowRight = faArrowRight;
  faCaretDown = faCaretDown;
  faCheck = faCheck;
  faTriangleExclamation = faTriangleExclamation;
  faRotateLeft = faRotateLeft;

  readonly relationshipTypeDescriptions: Record<string, string> = {
    Referenciador:
      'Únicamente identifica oportunidades y las deriva a SDi. Puede optar a una comisión del 10% del proyecto, pero nunca participa en el negocio recurrente.',
    Distribuidor:
      'Comercializa productos o servicios de SDi, pudiendo o no participar en la implantación. Puede participar en el negocio recurrente.',
    'Partner Estratégico':
      'Alianzas estratégicas que aportan valor al negocio, aunque no necesariamente exista una venta directa.',
    Colaborador:
      'Empresas o profesionales con los que SDi mantiene una relación de colaboración no remunerada, pudiendo compartir conocimiento, oportunidades, acciones, eventos o iniciativas cuando exista interés mutuo.',
  };

  readonly distributionModalityDescriptions: Record<string, string> = {
    Reventa: 'El distribuidor compra a SDi y factura al cliente final. SDi realiza la implantación y el soporte.',
    'Distribución Comercial':
      'El distribuidor únicamente comercializa. SDi factura al cliente final, y realiza la implantación y el soporte.',
    'Modelo Mixto':
      'La contratación, implantación y soporte se acuerdan según la operación o el producto concreto.',
    'Distribución Integral':
      'El distribuidor compra a SDi, factura al cliente final y se encarga también de la implantación y el soporte.',
  };

  readonly distributionModalityConditions: Record<
    string,
    { contracting: string; implementation: string; support: string }
  > = {
    Reventa: {
      contracting: 'Distribuidor',
      implementation: 'SDi',
      support: 'SDi',
    },
    'Distribución Comercial': {
      contracting: 'SDi',
      implementation: 'SDi',
      support: 'SDi',
    },
    'Modelo Mixto': {
      contracting: 'Según la operación',
      implementation: 'Según el producto',
      support: 'Según el producto',
    },
    'Distribución Integral': {
      contracting: 'Distribuidor',
      implementation: 'Distribuidor',
      support: 'Distribuidor',
    },
  };

  partner: Partner | null = null;
  files: PartnerFile[] = [];
  isLoading = true;
  isFilesLoading = false;
  showEditModal = false;
  showFilesModal = false;

  conditionLogs: PartnerConditionLog[] = [];
  isConditionLogsLoading = false;
  conditionLogsError = false;
  conditionLogsPage = 1;
  conditionLogsLastPage = 1;
  readonly conditionLogsPerPage = 5;

  showStatusMenu = false;
  isUpdatingStatus = false;
  readonly statusOptions: { value: Partner['status']; label: string }[] = [
    { value: 'active', label: 'Activo' },
    { value: 'inactive', label: 'Inactivo' },
  ];

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadPartner(id);
    }
  }

  loadPartner(id: string): void {
    this.isLoading = true;
    this.partnerService.getPartner(id).subscribe({
      next: (response) => {
        this.partner = response.data;
        this.isLoading = false;
        if (this.partner) {
          this.loadFiles();
          this.loadConditionLogs(this.partner.id);
        }
      },
      error: (err) => {
        console.error('Error loading partner detail:', err);
        this.isLoading = false;
      },
    });
  }

  loadConditionLogs(partnerId: string, page = 1): void {
    this.isConditionLogsLoading = true;
    this.conditionLogsError = false;
    this.partnerService
      .getConditionLogs(partnerId, { page, per_page: this.conditionLogsPerPage })
      .subscribe({
        next: (response) => {
          this.conditionLogs = page === 1
            ? response.data
            : [...this.conditionLogs, ...response.data];
          this.conditionLogsPage = response.meta.current_page;
          this.conditionLogsLastPage = response.meta.last_page;
          this.isConditionLogsLoading = false;
        },
        error: () => {
          this.conditionLogsError = true;
          this.isConditionLogsLoading = false;
        },
      });
  }

  loadMoreConditionLogs(): void {
    if (!this.partner || this.conditionLogsPage >= this.conditionLogsLastPage) return;
    this.loadConditionLogs(this.partner.id, this.conditionLogsPage + 1);
  }

  conditionLogFieldLabel(field: string): string {
    return PARTNER_CONDITION_FIELD_LABELS[field] ?? field;
  }

  toggleStatusMenu(): void {
    this.showStatusMenu = !this.showStatusMenu;
  }

  @HostListener('document:click')
  closeStatusMenu(): void {
    this.showStatusMenu = false;
  }

  changeStatus(status: Partner['status']): void {
    if (!this.partner || this.partner.status === status || this.isUpdatingStatus) return;
    this.isUpdatingStatus = true;
    this.partnerService.updateStatus(this.partner.id, status).subscribe({
      next: (response) => {
        this.partner = { ...this.partner!, status: response.data.status };
        this.isUpdatingStatus = false;
        this.showStatusMenu = false;
        this.popupInfoService.showSuccess('Estado del partner actualizado');
      },
      error: () => {
        this.isUpdatingStatus = false;
        this.popupInfoService.showError('Error al actualizar el estado del partner');
      },
    });
  }

  openEditModal(): void {
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
  }

  onEditSuccess(updatedPartner: Partner): void {
    this.partner = { ...this.partner, ...updatedPartner };
    this.closeEditModal();
  }

  loadFiles(): void {
    if (!this.partner) return;
    this.isFilesLoading = true;
    this.partnerService.getFiles(this.partner.id).subscribe({
      next: (files: PartnerFile[]) => {
        this.files = files;
        this.isFilesLoading = false;
      },
      error: () => {
        this.isFilesLoading = false;
      }
    });
  }

  openFilesModal(): void {
    this.showFilesModal = true;
  }

  closeFilesModal(): void {
    this.showFilesModal = false;
    this.loadFiles();
  }

  downloadFile(file: PartnerFile): void {
    this.partnerService.downloadFile(file).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      },
      error: () => this.popupInfoService.showError('Error al descargar el archivo')
    });
  }

  downloadContract(number: number, documentName: string | null): void {
    this.partnerService.downloadContract(number).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = documentName ?? `contrato-${number}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      },
      error: () => this.popupInfoService.showError('Error al descargar el contrato')
    });
  }

  deleteFile(fileId: number): void {
    if (confirm('¿Estás seguro de que quieres eliminar este archivo?')) {
      this.partnerService.deleteFile(fileId).subscribe({
        next: () => {
          this.files = this.files.filter(f => f.id !== fileId);
          this.popupInfoService.showSuccess('Archivo eliminado');
        },
        error: () => this.popupInfoService.showError('Error al eliminar el archivo')
      });
    }
  }

  formatBytes(bytes: number = 0): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatDate(date: string | undefined | null): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  formatCurrency(value: number | undefined | null): string {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  }
}
